import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { LockClosedIcon } from '@heroicons/react/24/outline';
import { useAuth } from '../../context/AuthContext';
import BackLink from '../../components/common/BackLink';
import apiService from '../../services/api';
import { League, Team, ScoringSystem, LeagueUpdate } from '../../types';

// Record-book ledger section head: an Anton masthead over a heavy ink rule.
function SectionHead({ title, danger = false }: { title: string; danger?: boolean }) {
  return (
    <div className={`border-b-2 ${danger ? 'border-danger-700' : 'border-ink-900'} pb-2 mb-5`}>
      <h2
        className={`font-display uppercase text-xl leading-none ${
          danger ? 'text-danger-700' : 'text-ink-900'
        }`}
      >
        {title}
      </h2>
    </div>
  );
}

const fieldLabel = 'block text-sm font-medium text-secondary-700 mb-1';

export default function LeagueSettingsPage() {
  const { leagueId } = useParams<{ leagueId: string }>();
  const id = leagueId ? parseInt(leagueId) : NaN;
  const { user } = useAuth();
  const navigate = useNavigate();

  const [league, setLeague] = useState<League | null>(null);
  const [teams, setTeams] = useState<Team[]>([]);
  const [scoringSystems, setScoringSystems] = useState<ScoringSystem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // General section fields (always editable)
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [isPublic, setIsPublic] = useState(false);
  const [waiversEnabled, setWaiversEnabled] = useState(false);
  const [generalSaving, setGeneralSaving] = useState(false);
  const [generalMsg, setGeneralMsg] = useState<{ ok: boolean; text: string } | null>(null);

  // League Structure section fields
  const [maxTeams, setMaxTeams] = useState('');
  const [entryFee, setEntryFee] = useState('');
  const [draftDate, setDraftDate] = useState('');
  const [scoringSystemId, setScoringSystemId] = useState('');
  const [structureSaving, setStructureSaving] = useState(false);
  const [structureMsg, setStructureMsg] = useState<{ ok: boolean; text: string } | null>(null);

  // Invite Code section
  const [inviteCode, setInviteCode] = useState<string | undefined>(undefined);
  const [inviteCopied, setInviteCopied] = useState(false);
  const [inviteSaving, setInviteSaving] = useState(false);
  const [inviteMsg, setInviteMsg] = useState<{ ok: boolean; text: string } | null>(null);

  // Transfer Commissioner section
  const [transferTo, setTransferTo] = useState('');
  const [transferSaving, setTransferSaving] = useState(false);
  const [transferMsg, setTransferMsg] = useState<{ ok: boolean; text: string } | null>(null);

  // Danger Zone — delete league
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [confirmText, setConfirmText] = useState('');
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  const syncFromLeague = (lg: League) => {
    setName(lg.name);
    setDescription(lg.description ?? '');
    setIsPublic(lg.is_public);
    setWaiversEnabled(lg.waivers_enabled);
    setMaxTeams(String(lg.max_teams));
    setEntryFee(String(lg.entry_fee));
    setDraftDate(lg.draft_date ? lg.draft_date.slice(0, 16) : '');
    setScoringSystemId(String(lg.scoring_system_id));
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [lg, tms, ss] = await Promise.all([
          apiService.getLeague(id),
          apiService.getTeams(id).catch(() => []),
          apiService.getScoringSystems().catch(() => []),
        ]);
        if (cancelled) return;
        if (lg.commissioner_id !== user?.id) {
          navigate(`/app/leagues/${id}`, { replace: true });
          return;
        }
        setLeague(lg);
        setTeams(tms);
        setScoringSystems(ss);
        setInviteCode(lg.invite_code);
        syncFromLeague(lg);
      } catch (e) {
        if (!cancelled) setError('Failed to load league settings.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [id, user?.id, navigate]);

  const handleSaveGeneral = async () => {
    setGeneralSaving(true);
    setGeneralMsg(null);
    try {
      const payload: LeagueUpdate = {
        name,
        description,
        is_public: isPublic,
        waivers_enabled: waiversEnabled,
      };
      const updated = await apiService.updateLeague(id, payload);
      setLeague(updated);
      syncFromLeague(updated);
      setGeneralMsg({ ok: true, text: 'Saved.' });
    } catch (e) {
      setGeneralMsg({ ok: false, text: 'Failed to save general settings.' });
    } finally {
      setGeneralSaving(false);
    }
  };

  const handleSaveStructure = async () => {
    setStructureSaving(true);
    setStructureMsg(null);
    try {
      const payload: LeagueUpdate = {
        max_teams: Number(maxTeams),
        entry_fee: Number(entryFee),
        draft_date: draftDate ? draftDate : null,
        scoring_system_id: Number(scoringSystemId),
      };
      const updated = await apiService.updateLeague(id, payload);
      setLeague(updated);
      syncFromLeague(updated);
      setStructureMsg({ ok: true, text: 'Saved.' });
    } catch (e) {
      setStructureMsg({ ok: false, text: 'Failed to save structure.' });
    } finally {
      setStructureSaving(false);
    }
  };

  const handleCopyInvite = () => {
    if (!inviteCode) return;
    navigator.clipboard
      .writeText(inviteCode)
      .then(() => {
        setInviteCopied(true);
        setTimeout(() => setInviteCopied(false), 2000);
      })
      .catch(() => {
        // silently ignore — clipboard not available
      });
  };

  const handleRegenerateInvite = async () => {
    setInviteSaving(true);
    setInviteMsg(null);
    try {
      const { invite_code } = await apiService.regenerateInviteCode(id);
      setInviteCode(invite_code);
      setInviteMsg({ ok: true, text: 'New code generated.' });
    } catch (e) {
      setInviteMsg({ ok: false, text: 'Failed to regenerate code.' });
    } finally {
      setInviteSaving(false);
    }
  };

  const handleTransfer = async () => {
    if (!transferTo) return;
    setTransferSaving(true);
    setTransferMsg(null);
    try {
      await apiService.transferCommissioner(id, Number(transferTo));
      navigate(`/app/leagues/${id}`);
    } catch (e) {
      setTransferMsg({ ok: false, text: 'Failed to transfer commissioner.' });
      setTransferSaving(false);
    }
  };

  const handleDelete = async () => {
    setDeleting(true);
    setDeleteError(null);
    try {
      await apiService.deleteLeague(id);
      navigate('/app/leagues');
    } catch (e) {
      setDeleteError('Failed to delete league. Please try again.');
      setDeleting(false);
    }
  };

  if (loading) {
    return (
      <div className="p-6 flex items-center">
        <div className="loading-spinner w-6 h-6" />
      </div>
    );
  }
  if (error) return <div className="p-6 text-danger-600">{error}</div>;
  if (!league) return null;

  const locked =
    league.league_status === 'active' || league.league_status === 'completed';

  const otherOwners = teams.filter((t) => t.user_id !== league.commissioner_id);
  const statusLabel = (league.league_status || 'setup').replace(/_/g, ' ');

  return (
    <div className="max-w-3xl mx-auto p-4 sm:p-6">
      <BackLink to={`/app/leagues/${id}`}>Back to league</BackLink>

      <header className="mt-4 flex flex-wrap items-end justify-between gap-x-6 gap-y-2 border-b-2 border-ink-900 pb-3">
        <div>
          <h1 className="font-display uppercase text-ink-900 text-4xl leading-none">
            League Settings
          </h1>
          <p className="mt-2 max-w-prose text-secondary-700">
            Manage{' '}
            <span className="font-semibold text-ink-900">{league.name}</span> — details,
            structure, invites, and commissioner controls.
          </p>
        </div>
        <span className="text-xs font-semibold uppercase tracking-[0.18em] text-secondary-600 whitespace-nowrap">
          {statusLabel} league
        </span>
      </header>

      <div className="mt-10 space-y-12 sm:mt-12 sm:space-y-14">
        {/* General */}
        <section>
          <SectionHead title="General" />
          <div className="space-y-5">
            <div>
              <label htmlFor="ls-name" className={fieldLabel}>
                League name
              </label>
              <input
                id="ls-name"
                aria-label="League name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="form-input w-full"
              />
            </div>
            <div>
              <label htmlFor="ls-desc" className={fieldLabel}>
                Description
              </label>
              <textarea
                id="ls-desc"
                aria-label="Description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                rows={3}
                placeholder="What's this league about?"
                className="form-input w-full"
              />
            </div>
            <label className="flex items-center gap-2.5">
              <input
                type="checkbox"
                aria-label="Public league"
                checked={isPublic}
                onChange={(e) => setIsPublic(e.target.checked)}
                className="h-4 w-4"
              />
              <span className="text-sm font-medium text-secondary-700">
                List publicly in league discovery
              </span>
            </label>
            <label className="flex items-center gap-2.5">
              <input
                type="checkbox"
                aria-label="Waivers mode"
                checked={waiversEnabled}
                onChange={(e) => setWaiversEnabled(e.target.checked)}
                className="h-4 w-4"
              />
              <span className="text-sm font-medium text-secondary-700">
                Waivers mode — route free-agent pickups through a claims queue the
                commissioner processes
              </span>
            </label>
            <div className="flex items-center gap-4 pt-1">
              <button
                type="button"
                className="btn-primary"
                onClick={handleSaveGeneral}
                disabled={generalSaving}
              >
                {generalSaving ? 'Saving…' : 'Save General'}
              </button>
              {generalMsg && (
                <span
                  className={`text-sm ${generalMsg.ok ? 'text-success-700' : 'text-danger-600'}`}
                  role="status"
                >
                  {generalMsg.text}
                </span>
              )}
            </div>
          </div>
        </section>

        {/* League Structure */}
        <section>
          <SectionHead title="League Structure" />
          {locked && (
            <div className="mb-6 flex items-start gap-2.5 border border-secondary-300 bg-secondary-100 px-3.5 py-3">
              <LockClosedIcon className="mt-0.5 h-4 w-4 flex-shrink-0 text-secondary-600" />
              <p className="text-sm text-secondary-700">
                <span className="font-semibold">Locked while the season is underway.</span>{' '}
                Roster size, entry fee, draft date, and scoring can’t change once a league
                is active.
              </p>
            </div>
          )}
          <div className={locked ? 'opacity-60' : ''}>
            <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
              <div>
                <label htmlFor="ls-maxteams" className={fieldLabel}>
                  Max teams
                </label>
                <input
                  id="ls-maxteams"
                  type="number"
                  aria-label="Max teams"
                  value={maxTeams}
                  onChange={(e) => setMaxTeams(e.target.value)}
                  disabled={locked}
                  className="form-input w-full"
                />
              </div>
              <div>
                <label htmlFor="ls-entryfee" className={fieldLabel}>
                  Entry fee
                </label>
                <input
                  id="ls-entryfee"
                  type="number"
                  aria-label="Entry fee"
                  value={entryFee}
                  onChange={(e) => setEntryFee(e.target.value)}
                  disabled={locked}
                  className="form-input w-full"
                />
              </div>
              <div>
                <label htmlFor="ls-draftdate" className={fieldLabel}>
                  Draft date
                </label>
                <input
                  id="ls-draftdate"
                  type="datetime-local"
                  aria-label="Draft date"
                  value={draftDate}
                  onChange={(e) => setDraftDate(e.target.value)}
                  disabled={locked}
                  className="form-input w-full"
                />
              </div>
              <div>
                <label htmlFor="ls-scoring" className={fieldLabel}>
                  Scoring system
                </label>
                <select
                  id="ls-scoring"
                  aria-label="Scoring system"
                  value={scoringSystemId}
                  onChange={(e) => setScoringSystemId(e.target.value)}
                  disabled={locked}
                  className="form-select w-full"
                >
                  {scoringSystems.map((s) => (
                    <option key={s.id} value={s.id}>
                      {s.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <div className="mt-5 flex items-center gap-4">
              <button
                type="button"
                className="btn-primary"
                onClick={handleSaveStructure}
                disabled={locked || structureSaving}
              >
                {structureSaving ? 'Saving…' : 'Save Structure'}
              </button>
              {structureMsg && (
                <span
                  className={`text-sm ${structureMsg.ok ? 'text-success-700' : 'text-danger-600'}`}
                  role="status"
                >
                  {structureMsg.text}
                </span>
              )}
            </div>
          </div>
        </section>

        {/* Invite Code */}
        <section>
          <SectionHead title="Invite Code" />
          <div className="flex flex-col gap-4 sm:flex-row sm:items-end">
            <div className="sm:max-w-xs sm:flex-1">
              <label htmlFor="ls-invite" className={fieldLabel}>
                Current code
              </label>
              <input
                id="ls-invite"
                aria-label="Invite code"
                value={inviteCode ?? ''}
                readOnly
                className="form-input w-full font-display tracking-[0.2em] text-ink-900"
              />
            </div>
            <div className="flex items-center gap-3">
              <button
                type="button"
                className="btn-outline-secondary"
                onClick={handleCopyInvite}
                disabled={!inviteCode}
              >
                {inviteCopied ? 'Copied!' : 'Copy'}
              </button>
              <button
                type="button"
                className="btn-primary"
                onClick={handleRegenerateInvite}
                disabled={inviteSaving}
              >
                {inviteSaving ? 'Regenerating…' : 'Regenerate Code'}
              </button>
            </div>
          </div>
          {inviteMsg && (
            <span
              className={`mt-3 block text-sm ${inviteMsg.ok ? 'text-success-700' : 'text-danger-600'}`}
              role="status"
            >
              {inviteMsg.text}
            </span>
          )}
        </section>

        {/* Transfer Commissioner */}
        <section>
          <SectionHead title="Transfer Commissioner" />
          {otherOwners.length === 0 ? (
            <p className="text-sm text-secondary-700">
              No other team owners to transfer to yet. Once someone joins, you can hand the
              league over here.
            </p>
          ) : (
            <div className="flex flex-col gap-4 sm:flex-row sm:items-end">
              <div className="sm:max-w-xs sm:flex-1">
                <label htmlFor="ls-transfer" className={fieldLabel}>
                  New commissioner
                </label>
                <select
                  id="ls-transfer"
                  aria-label="Transfer to"
                  value={transferTo}
                  onChange={(e) => setTransferTo(e.target.value)}
                  className="form-select w-full"
                >
                  <option value="">Select a team owner…</option>
                  {otherOwners.map((t) => (
                    <option key={t.id} value={t.user_id}>
                      {t.team_name}
                    </option>
                  ))}
                </select>
              </div>
              <div className="flex items-center gap-4">
                <button
                  type="button"
                  className="btn-primary"
                  onClick={handleTransfer}
                  disabled={transferSaving || !transferTo}
                >
                  {transferSaving ? 'Transferring…' : 'Transfer Commissioner'}
                </button>
                {transferMsg && (
                  <span className="text-sm text-danger-600" role="status">
                    {transferMsg.text}
                  </span>
                )}
              </div>
            </div>
          )}
        </section>

        {/* Danger Zone — the one deliberately-bordered block */}
        <section className="border border-danger-300 bg-danger-50 px-5 py-5 sm:px-6 sm:py-6">
          <SectionHead title="Danger Zone" danger />
          <p className="max-w-prose text-sm text-secondary-700">
            Deleting this league permanently removes it and all of its teams, rosters, and
            matchups. This cannot be undone.
          </p>
          <button
            type="button"
            className="btn-danger mt-4"
            onClick={() => {
              setConfirmText('');
              setDeleteError(null);
              setShowDeleteModal(true);
            }}
          >
            Delete League
          </button>
        </section>
      </div>

      {showDeleteModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-ink-900/60 p-4">
          <div className="w-full max-w-md border border-secondary-300 bg-white p-6 shadow-xl">
            <h3 className="font-display uppercase text-xl leading-none text-danger-700">
              Delete League
            </h3>
            <p className="mt-3 text-sm text-secondary-700">
              This action is permanent and cannot be undone. Type the league name{' '}
              <span className="font-semibold text-ink-900">{league.name}</span> to confirm.
            </p>
            <input
              type="text"
              aria-label="Type the league name to confirm"
              value={confirmText}
              onChange={(e) => setConfirmText(e.target.value)}
              className="form-input mt-4 w-full"
            />
            {deleteError && (
              <p className="mt-3 text-sm text-danger-600" role="status">
                {deleteError}
              </p>
            )}
            <div className="mt-5 flex gap-3">
              <button
                type="button"
                className="btn-outline-secondary flex-1"
                onClick={() => setShowDeleteModal(false)}
                disabled={deleting}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn-danger flex-1"
                onClick={handleDelete}
                disabled={deleting || confirmText !== league.name}
              >
                {deleting ? 'Deleting…' : 'Confirm Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
